import React, { useCallback, useEffect } from "react";
import { connect } from "react-redux";

import MapTool from "@mapmycustomers/shared/enum/map/MapTool";

import { useMap } from "@app/component/map/FeaturedMap";
import Drafter from "@app/scene/map/components/tools/LassoTool/components/Drafter";
import Path from "@app/scene/map/components/tools/LassoTool/components/Path";
import { appendLassoPath } from "@app/scene/map/store/actions";
import {
  getMapLassoAllowAdd,
  getMapLassoPaths,
  getMapViewTool,
} from "@app/scene/map/store/selectors";
import { RootState } from "@app/store/rootReducer";
import GeoPath from "@app/types/GeoPath";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import useAnalytics from "@app/util/contexts/useAnalytics";

interface Props {
  allowAdd: boolean;
  appendLassoPath: typeof appendLassoPath;
  pathLimit?: number;
  paths: Array<GeoPath>;
  tool?: MapTool;
}

const LassoTool: React.FC<Props> = ({ allowAdd, appendLassoPath, pathLimit, paths, tool }) => {
  const analytics = useExtendedAnalytics("Lasso Tool", useAnalytics());
  const { map } = useMap();

  const isActive = map && tool === MapTool.LASSO;
  useEffect(() => {
    if (isActive) {
      analytics.initiated();
    }
  }, [analytics, isActive]);

  const handleCancel = useCallback(() => {
    analytics.cancelled(["Created Shape", "Exit Lasso"]);
  }, [analytics]);

  const handleAdd = useCallback(
    (path: GeoPath) => {
      analytics.using(["Created Shape"]);
      if (isActive) {
        appendLassoPath(path);
      }
    },
    [analytics, appendLassoPath, isActive]
  );

  const couldAdd = allowAdd && paths.length < (pathLimit ?? Number.MAX_VALUE);

  return isActive ? (
    <>
      {couldAdd && <Drafter onAdd={handleAdd} onCancel={handleCancel} />}
      {paths.map((path, index) => (
        <Path key={index} path={path} />
      ))}
    </>
  ) : null;
};

const mapStateToProps = (state: RootState) => ({
  allowAdd: getMapLassoAllowAdd(state),
  paths: getMapLassoPaths(state),
  tool: getMapViewTool(state),
});

const mapDispatchToProps = {
  appendLassoPath,
};

export default connect(mapStateToProps, mapDispatchToProps)(LassoTool);
